'use client';

import { Hash, ChevronDown } from 'lucide-react';
import { Avatar } from '@/components/ds';
import { cn } from '@/lib/utils';

type Channel = { id: string; name: string };

type Props = {
  channels: Channel[];
  activeChannelId: string;
  onSelect: (id: string) => void;
  selfName: string;
  selfAvatarUrl?: string | null;
};

export default function ChannelsPanel({
  channels,
  activeChannelId,
  onSelect,
  selfName,
  selfAvatarUrl,
}: Props) {
  return (
    <div className="h-full flex flex-col bg-surface-1 border-r border-border-subtle">
      <div className="px-4 pt-3 pb-2">
        <span className="text-[11px] uppercase tracking-wider font-mono text-text-tertiary">
          Channels
        </span>
      </div>
      <ul className="flex-1 overflow-y-auto pb-2">
        {channels.map((c) => {
          const active = c.id === activeChannelId;
          return (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onSelect(c.id)}
                aria-pressed={active}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-1.5 rounded-sm transition-colors text-left',
                  active
                    ? 'bg-accent-soft text-accent'
                    : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary',
                )}
              >
                <Hash className="w-3.5 h-3.5" strokeWidth={1.75} />
                <span className="text-sm">{c.name}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Footer presence — static label, chevron is decorative (Phase 2 will wire menu) */}
      <div className="px-3 py-2 border-t border-border-subtle flex items-center gap-2">
        <Avatar src={selfAvatarUrl || undefined} name={selfName} size={28} />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-text-primary truncate">{selfName}</p>
          <p className="text-[11px] text-text-tertiary flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-status-online" />
            Online
          </p>
        </div>
        <ChevronDown className="w-3.5 h-3.5 text-text-tertiary" strokeWidth={1.75} aria-hidden />
      </div>
    </div>
  );
}
